// Sign-in / create-account page (split screen) and the access-denied page.
//
// Left: a RouteZero brand panel (decorative, aria-hidden): three frosted "lab" cards that float
// and a chart whose bars breathe, plus the RouteZero method as a quote. Right: the form.
// With Supabase configured: real accounts (src/ui/account-views.js — sign in, create account,
// forgot / reset password; admins are accounts with the admin role). Without it (local development,
// automated tests, the preview artifact): the MOCK sign-in (src/auth/mockAuthProvider.js) — a
// display name, no real accounts, no password check. The form says so.
import { h } from './h.js';
import { logoFull, BRAND } from './brand.js';
import { icon } from './icons.js';
import { prefersReducedMotion } from './carousel.js';
import { REMEMBER_TTL_MS } from '../auth/session.js';
import { accountAuthView } from './account-views.js';
import { signupTestimonial } from './testimonials-views.js';

const fast = () => typeof window !== 'undefined' && !!window.__NELL_FAST__;

/** Decorative brand panel. Illustrative only — RouteZero Technologies is the fictional company of the labs. */
export function brandPanel() {
  const bars = [0.42, 0.58, 0.5, 0.71, 0.64, 0.86, 1];
  return h('div', { class: 'rz-auth-art', 'aria-hidden': 'true', 'data-testid': 'auth-brand-panel' },
    h('div', { class: 'rz-auth-name' }, BRAND.name),
    h('div', { class: 'rz-auth-stack' },
      h('div', { class: 'rz-auth-glass g1' },
        h('span', { class: 'rz-auth-gi' }, icon('check', { size: 15, stroke: 2.6 })),
        h('p', {}, h('b', {}, 'Ticket TK-48291 resolved'), h('small', {}, 'Connectivity verified · just now')),
        h('em', {}, 'Level 1')),
      h('div', { class: 'rz-auth-glass g2' },
        h('p', { class: 'rz-auth-gk' }, 'Missions completed this week'),
        h('p', { class: 'rz-auth-gv' }, '12 ', h('span', {}, icon('trend', { size: 14 }), '+3')),
        h('div', { class: 'rz-auth-bars' }, bars.map((v, i) => h('i', { style: `--h:${v};--d:${i * 0.12}s` })))),
      h('div', { class: 'rz-auth-glass g3' },
        h('span', { class: 'rz-auth-avs' }, ['AL', 'TK', 'RS'].map((t) => h('i', {}, t))),
        h('p', {}, h('b', {}, '3 RouteZero engineers on shift'), h('small', {}, 'Iloilo NOC · reviewing the change window')))),
    h('figure', { class: 'rz-auth-quote' },
      h('blockquote', {}, '“Investigate before you change anything. Verify before you close the ticket.”'),
      h('figcaption', {}, h('b', {}, 'The RouteZero method'), 'From Level 1 support to network engineering'),
      h('p', { class: 'rz-auth-note' }, 'Illustrative preview · RouteZero Technologies is a fictional company')));
}

/**
 * Sign-in page. With real accounts (Supabase configured) this is the email + password form in
 * src/ui/account-views.js. Without them it is the DEVELOPMENT mock below: mode 'create' is the
 * same mock sign-in, worded for a new learner.
 */
export function loginView(auth, opts = {}) {
  if (auth.accounts) return accountAuthView(auth, opts);
  return mockLoginView(auth, opts);
}

function mockLoginView(auth, { onDone, mode = 'login', guest = null, notice = null, testimonials = null } = {}) {
  const create = mode === 'create';
  const reduce = prefersReducedMotion();
  const err = h('div', { class: 'rz-auth-err form-err', role: 'alert', 'data-testid': 'login-error' });

  // ---- fields
  const name = h('input', { id: 'login-name', name: 'username', autocomplete: 'username', placeholder: 'e.g. Gil', 'data-testid': 'login-name', maxlength: 40, 'aria-describedby': 'login-name-err', 'aria-invalid': 'false' });
  const nameErr = h('p', { class: 'rz-auth-ferr', id: 'login-name-err', role: 'alert', 'data-testid': 'login-name-err' });
  const pass = h('input', { id: 'login-pass', name: 'password', type: 'password', autocomplete: create ? 'new-password' : 'current-password', placeholder: 'Any password', 'data-testid': 'login-pass', 'aria-describedby': 'login-pass-hint' });
  const eyeBtn = h('button', { class: 'rz-auth-eye', type: 'button', 'aria-label': 'Show password', 'aria-pressed': 'false', 'aria-controls': 'login-pass', 'data-testid': 'login-pass-toggle' }, icon('eye', { size: 17, cls: 'i-show' }), icon('eyeOff', { size: 17, cls: 'i-hide' }));
  eyeBtn.addEventListener('click', () => {
    const show = pass.type === 'password';
    pass.type = show ? 'text' : 'password';
    eyeBtn.classList.toggle('on', show);
    eyeBtn.setAttribute('aria-pressed', String(show));
    eyeBtn.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
  });
  const remember = h('input', { id: 'login-remember', type: 'checkbox', 'data-testid': 'login-remember' });
  const ack = h('input', { id: 'admin-ack', type: 'checkbox', 'data-testid': 'admin-ack' });
  const nameField = h('div', { class: 'rz-auth-field', 'data-f': 'name' }, icon('user', { size: 17 }), name);

  // ---- validation (novalidate: our own messages, announced via role="alert")
  function checkName() {
    const v = name.value.trim();
    const msg = !v ? 'Enter a name to sign in.' : v.length > 40 ? 'Names are limited to 40 characters.' : '';
    nameField.classList.toggle('bad', !!msg);
    name.setAttribute('aria-invalid', String(!!msg));
    if (nameErr.textContent !== msg) nameErr.textContent = msg;
    if (msg) { nameField.classList.remove('shake'); void nameField.offsetWidth; nameField.classList.add('shake'); }
    return !msg;
  }
  name.addEventListener('blur', () => { if (name.value) checkName(); });
  name.addEventListener('input', () => { if (nameField.classList.contains('bad') && name.value.trim()) { nameField.classList.remove('bad'); name.setAttribute('aria-invalid', 'false'); nameErr.textContent = ''; } });
  nameField.addEventListener('animationend', () => nameField.classList.remove('shake'));

  // ---- submit: label slides (Sign in → Signing in), sheen instead of a spinner, then green "Signed in"
  const label = create ? ['Create account & continue', 'Creating your account', 'Account ready'] : ['Sign in', 'Signing in', 'Signed in'];
  const go = h('button', { class: 'rz-auth-go', type: 'submit', 'data-testid': 'login-learner' },
    h('span', { class: 'lbl' }, label[0]),
    h('span', { class: 'busy', 'aria-hidden': 'true' }, label[1]),
    h('span', { class: 'done', 'aria-hidden': 'true' }, icon('check', { size: 17, stroke: 2.6 }), label[2]));
  const wait = (ms) => new Promise((r) => setTimeout(r, fast() || reduce ? 0 : ms));
  let busy = false;
  async function submit(role) {
    if (busy) return;
    err.textContent = '';
    if (role === 'learner' && !checkName()) { name.focus(); return; }
    busy = true;
    const btn = role === 'learner' ? go : null;
    btn?.classList.add('loading'); btn?.setAttribute('aria-busy', 'true');
    try {
      await wait(650);
      await auth.login({ username: name.value, password: pass.value, role, acknowledgeTestMode: ack.checked }, remember.checked ? { ttlMs: REMEMBER_TTL_MS } : {});
      if (btn) { btn.classList.replace('loading', 'ok'); btn.setAttribute('aria-label', label[2]); await wait(550); }
      onDone(role);
    } catch (e) {
      btn?.classList.remove('loading', 'ok'); btn?.removeAttribute('aria-busy');
      err.textContent = e.message;
      if (e.code === 'invalid') checkName();
    } finally { busy = false; }
  }

  const devBox = h('div', { class: 'dev-box rz-auth-dev', role: 'note', id: 'rz-auth-dev' },
    h('div', { class: 'rc-title' }, 'DEVELOPMENT BUILD · MOCK SIGN-IN'),
    h('p', { class: 'small' }, 'Phase A has no server, so there are no real accounts and no password is checked. This screen is a placeholder for POST /api/auth/login.'),
    h('label', { class: 'radio small', for: 'admin-ack' }, ack, ' I understand ADMIN / DEVELOPMENT TEST MODE is for testing labs and uses a separate sandbox, not learner progress.'),
    h('button', { class: 'btn danger small', type: 'button', 'data-testid': 'login-admin', onclick: () => submit('admin') }, 'Enter Admin Test Mode'));

  const form = h('form', { class: 'rz-auth-form', novalidate: true, 'aria-labelledby': 'rz-auth-title', onsubmit: (e) => { e.preventDefault(); submit('learner'); } },
    h('div', { class: 'login-logo' }, logoFull({ width: 180 })),
    h('h1', { id: 'rz-auth-title' }, create ? 'Create your free account' : 'Welcome back'),
    notice ? h('p', { class: 'rz-auth-notice', role: 'status', 'data-testid': 'auth-notice' }, notice) : null,
    h('p', { class: 'rz-auth-lead' }, create ? 'Free, no payment required. Keep your progress from IT support into network engineering.' : 'Sign in to continue your RouteZero journey.'),
    guest?.any ? h('p', { class: 'rz-guest-note', role: 'note', 'data-testid': 'login-guest-note' }, `You have free Support Desk work in this browser (${guest.completed} completed ticket${guest.completed === 1 ? '' : 's'}${guest.practiceCompleted ? '; practice ticket complete' : guest.practiceStarted ? '; practice ticket in progress' : ''}). After you sign in, RouteZero will ask whether to continue where you left off.`) : null,
    h('div', { class: 'rz-auth-alt' },
      h('a', { class: 'rz-auth-ghost', href: '#/support', 'data-testid': 'login-try-support' }, icon('headset', { size: 17 }), 'Free Support Desk'),
      h('button', { class: 'rz-auth-ghost', type: 'button', 'data-testid': 'login-dev-jump', onclick: () => { devBox.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); ack.focus({ preventScroll: true }); } }, icon('gear', { size: 17 }), 'Admin test mode')),
    h('p', { class: 'rz-auth-or' }, h('span', {}, 'or')),
    h('label', { class: 'rz-auth-fl', for: 'login-name' }, 'Name'),
    nameField, nameErr,
    h('div', { class: 'rz-auth-fl row' }, h('label', { for: 'login-pass' }, 'Password'), h('span', { class: 'rz-auth-hint', id: 'login-pass-hint' }, 'Not checked in this preview')),
    h('div', { class: 'rz-auth-field', 'data-f': 'pass' }, icon('lock', { size: 17 }), pass, eyeBtn),
    h('label', { class: 'rz-auth-keep', for: 'login-remember' }, remember, h('span', { class: 'rz-auth-box', 'aria-hidden': 'true' }, icon('check', { size: 12, stroke: 3 })), 'Keep me signed in for 30 days'),
    err,
    go,
    h('p', { class: 'rz-auth-new' }, create ? 'Already have an account? ' : `New to ${BRAND.name}? `,
      h('a', { href: create ? '#/login' : '#/signup', 'data-testid': create ? 'login-switch-signin' : 'login-switch-create' }, create ? 'Sign in' : 'Create a free account')),
    create ? signupTestimonial(testimonials) : null,
    devBox,
    h('p', { class: 'small muted login-back' }, h('a', { href: '#/', 'data-testid': 'login-back-public' }, '← Back to the RouteZero home page')));

  return h('main', { class: `page login rz-auth${create ? ' is-create' : ''}`, 'data-testid': 'auth-page' },
    brandPanel(),
    h('div', { class: 'rz-auth-side' }, form));
}

export function deniedView(what = 'This page') {
  return h('main', { class: 'page' }, h('section', { class: 'panel locked-page', 'data-testid': 'access-denied' },
    h('h1', {}, '⛔ Admin only'),
    h('p', { class: 'muted' }, `${what} is only available in ADMIN / DEVELOPMENT TEST MODE. You are signed in as a learner, so your normal progression rules apply.`),
    h('a', { class: 'btn primary', href: '#/' }, 'Back to Main Menu')));
}
